import { videos, typeCategories, genreOptions, regionOptions, yearOptions } from "@/mocks/videos";
import type { Video, VideoQuery } from "@/types/video";

const allVideos = videos as unknown as Video[];

export const PAGE_SIZE = 12;

export function getAllVideos(): Video[] {
  return allVideos;
}

export function getVideoById(id: string): Video | undefined {
  return allVideos.find((item) => item.id === id);
}

/** 将路由中的分类参数归一化为中文分类名 */
export function resolveCategoryType(param?: string): string | undefined {
  if (!param) return undefined;
  const decoded = decodeURIComponent(param);
  const matched = typeCategories.find((c) => c.key === decoded || c.slug === decoded);
  return matched ? matched.key : decoded;
}

export function getCategoryInfo(param?: string) {
  const type = resolveCategoryType(param);
  return typeCategories.find((c) => c.key === type);
}

function matchesKeyword(video: Video, keyword: string): boolean {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return true;
  const haystack = [
    video.title,
    video.type,
    video.region,
    video.director,
    ...video.genre,
    ...video.actors,
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(kw);
}

function isValid(value?: string): boolean {
  return !!value && value !== "全部" && value !== "all";
}

export function queryVideos(query: VideoQuery): Video[] {
  let list = allVideos.slice();

  if (isValid(query.type)) {
    const type = resolveCategoryType(query.type);
    list = list.filter((item) => item.type === type);
  }
  if (isValid(query.region)) {
    list = list.filter((item) => item.region === query.region);
  }
  if (isValid(query.year)) {
    list = list.filter((item) => String(item.year) === query.year);
  }
  if (isValid(query.genre)) {
    list = list.filter((item) => item.genre.includes(query.genre as string));
  }
  if (query.keyword) {
    list = list.filter((item) => matchesKeyword(item, query.keyword as string));
  }

  const sort = query.sort || "latest";
  if (sort === "hot") {
    list.sort((a, b) => b.heat - a.heat);
  } else if (sort === "rating") {
    list.sort((a, b) => b.rating - a.rating);
  } else {
    list.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }

  return list;
}

export function paginate<T>(list: T[], page: number, size = PAGE_SIZE) {
  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / size));
  const current = Math.min(Math.max(1, page), totalPages);
  const start = (current - 1) * size;
  return {
    items: list.slice(start, start + size),
    total,
    totalPages,
    currentPage: current,
  };
}

export function getLatestVideos(limit = 8): Video[] {
  return queryVideos({ sort: "latest" }).slice(0, limit);
}

export function getHotVideos(limit = 8): Video[] {
  return queryVideos({ sort: "hot" }).slice(0, limit);
}

export function searchVideos(keyword: string): Video[] {
  if (!keyword.trim()) return [];
  return queryVideos({ keyword, sort: "hot" });
}

export function getRelatedVideos(video: Video, limit = 6): Video[] {
  const scored = allVideos
    .filter((item) => item.id !== video.id)
    .map((item) => {
      let score = 0;
      if (item.type === video.type) score += 3;
      if (item.region === video.region) score += 1;
      score += item.genre.filter((g) => video.genre.includes(g)).length * 2;
      return { item, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.item.heat - a.item.heat);

  const result = scored.slice(0, limit).map((entry) => entry.item);
  if (result.length < limit) {
    const extra = allVideos
      .filter((item) => item.id !== video.id && !result.some((r) => r.id === item.id))
      .sort((a, b) => b.heat - a.heat)
      .slice(0, limit - result.length);
    return [...result, ...extra];
  }
  return result;
}

export { typeCategories, genreOptions, regionOptions, yearOptions };